import { useEffect, useState, useRef } from 'react';
import { Card, Col, Form, Row, Upload, Input, notification } from 'antd';
import type { UploadFile } from 'antd/es/upload/interface';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Button from 'app/atoms/Button';
import ContactInput from 'app/atoms/ContactInput';
import SearchBar from 'app/atoms/SearchBar';
import ConfirmationModal from 'app/molecules/ConfirmationModal';
import FormSkeleton from 'app/atoms/FormSkeleton';
import { validateUploadSize } from 'utils/validateUploadSize';
import { getProfilePictureFileList } from 'utils/file';
import { customizeRequiredMark, fieldDefaultProps } from 'utils/fieldConfig';
import { useCountries } from 'app/hooks/useCountries';
import { useUserInfo, USER_INFO_QUERY_KEY } from 'app/hooks/useUserInfo';
import { useSSO } from 'app/hooks/useSSO';
import { CountryCode } from 'libphonenumber-js';
import { DATE_FORMAT, DEFAULT_COUNTRY, IMAGE_TYPE_WITHOUT_PDF, MAX_PROFILE_PICTURE_COUNT } from 'configs/constants';
import { CloseOutlined, UploadOutlined, UserOutlined } from '@ant-design/icons';
import MicrosoftLogo from './../../../assets/microsoft-logo.svg?react';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { updateProfile } from './services';
import { ErrorMessage, getErrorMessage } from 'utils/axios';
import { NotificationProps } from 'utils/types';
import { FileUploadEvent, PreviewFile, ProfileFormValues, ProfileUpdateResponse } from './types';
import { useProfileHandlers } from './util/profileHandlers';
import { UserInfoResponse } from 'app/modules/Auth/types';
import { MAX_FILE_SIZE_MB } from 'configs';

const getFile = (e: FileUploadEvent): UploadFile[] | undefined => {
  if (Array.isArray(e)) {
    return e;
  }
  return e?.fileList;
};

export default function ProfileSetting() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<ProfileFormValues>();
  const [isDiscardModalOpen, setIsDiscardModalOpen] = useState(false);
  const navigate = useNavigate();
  const { data: userInfo, isLoading } = useUserInfo();
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const shouldUpdateFormRef = useRef(false);
  const [waitingForUpdate, setWaitingForUpdate] = useState(false);
  const [lastUserInfoHash, setLastUserInfoHash] = useState<string>('');

  const { ssoState, toggleSSO, removeSSO, initializeSSO } = useSSO();

  const [selectedCountry, setSelectedCountry] = useState<{ code: CountryCode; iso: string | undefined }>({
    code: DEFAULT_COUNTRY.CODE as CountryCode,
    iso: DEFAULT_COUNTRY.ISO,
  });

  const { data: countries } = useCountries();

  // Add mutation for updating profile
  const updateMutation = useMutation<ProfileUpdateResponse, ErrorMessage, FormData>({
    mutationKey: ['update-profile'],
    mutationFn: updateProfile,
    onSuccess: async (data: ProfileUpdateResponse) => {
      // Check if the response indicates the user should be logged out
      if (data?.logout === true) {
        await handleServerLogout();
        return;
      }

      notification.success({
        message: t('apiSuccessMessages.updateProfile'),
        ...NotificationProps,
      });
      queryClient.invalidateQueries({ queryKey: USER_INFO_QUERY_KEY });
      form.setFieldsValue({ _formChanged: false });
      setIsFormChanged(false);
      setPreviewImage(null);
      shouldUpdateFormRef.current = true; // Mark that form should be updated after refetch
      setWaitingForUpdate(true); // Mark that we're waiting for updated data
    },
    onError: (error: ErrorMessage) => {
      notification.error({
        message: getErrorMessage(error),
        ...NotificationProps,
      });
    },
  });

  // Create a hash of userInfo to detect changes
  const createUserInfoHash = (userInfo: UserInfoResponse | undefined) => {
    if (!userInfo) return '';
    return JSON.stringify({
      contactNumber: userInfo.contactNumber,
      phoneCountryCode: userInfo.phoneCountryCode,
      phoneCountryIso: userInfo.phoneCountryIso,
    });
  };

  // Set form values when user info is loaded
  useEffect(() => {
    if (userInfo) {
      const currentHash = createUserInfoHash(userInfo);

      // Only update if we're waiting for an update and the data has actually changed
      // OR if this is the initial load
      if ((waitingForUpdate && currentHash !== lastUserInfoHash) || !form.getFieldValue('name')) {
        const cleanedUpSingleObject: ProfileFormValues = {};

        cleanedUpSingleObject.name = userInfo.fullName ?? '';
        cleanedUpSingleObject.surname = userInfo.surName ?? '';
        cleanedUpSingleObject.email = userInfo.email ?? '';
        cleanedUpSingleObject.cpf = userInfo.cpf ?? '';
        cleanedUpSingleObject.employeeId = userInfo.employeeId ?? '';
        cleanedUpSingleObject.contactNumber = userInfo.contactNumber ?? '';
        cleanedUpSingleObject.department = userInfo.department ?? '';
        cleanedUpSingleObject.roles =
          userInfo.roles?.map((role) => (typeof role === 'string' ? role : role.name)) ?? [];
        cleanedUpSingleObject.registrationNumber = userInfo.registrationNumber ?? '';
        cleanedUpSingleObject.dob = userInfo.dob ? dayjs(userInfo.dob) : undefined;

        if (userInfo.photo) {
          cleanedUpSingleObject.profilePicture = getProfilePictureFileList(userInfo.photo);
        }

        initializeSSO(userInfo.ssoEnabled ?? false);

        if (userInfo.phoneCountryCode && userInfo.phoneCountryIso) {
          setSelectedCountry({
            iso: userInfo.phoneCountryIso,
            code: (userInfo.phoneCountryCode ?? DEFAULT_COUNTRY.CODE) as CountryCode,
          });
        }

        form.setFieldsValue(cleanedUpSingleObject);
        shouldUpdateFormRef.current = false; // Reset the flag after updating
        setWaitingForUpdate(false); // Reset waiting state
        setLastUserInfoHash(currentHash); // Update the hash
      }
    }
  }, [userInfo, form, initializeSSO, waitingForUpdate, lastUserInfoHash]);

  useEffect(() => {
    if (selectedCountry.code) {
      const country = getCountryByCode(selectedCountry.code);
      if (country) {
        setSelectedCountry((prev) => ({
          ...prev,
          iso: country.isoAlpha2,
        }));
      }
    }
  }, [selectedCountry.code]);

  const getCountryByCode = (countryCode: string) => {
    return countries?.data?.find((country) => country.phoneCountryCode === countryCode);
  };

  // Set header props when component mounts
  useEffect(() => {
    queryClient.setQueryData(['header-props'], {
      selectedMenuItem: t('headers.home'),
      detailTitle: t('header.profile'),
    });
  }, [queryClient, t]);

  // Track form changes without updating form values
  const [isFormChanged, setIsFormChanged] = useState(false);

  // Add this function to handle preview
  const handlePreview = (file: PreviewFile) => {
    if (file?.originFileObj) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewImage(e.target?.result as string);
      };
      reader.readAsDataURL(file.originFileObj);
    }
  };

  const { handleSubmit, handleDiscard, handleServerLogout, handleSSOToggle, handleSSODisable } = useProfileHandlers(
    form,
    userInfo || {},
    selectedCountry,
    ssoState,
    toggleSSO,
    removeSSO,
    setPreviewImage,
    setIsFormChanged,
    setIsDiscardModalOpen,
    updateMutation,
  );

  return (
    <div className="space-y-6">
      <SearchBar onSearch={() => {}} />

      <Card>
        {isLoading ? (
          <FormSkeleton rows={4} cols={2} showSubmitButton />
        ) : (
          <Form
            form={form}
            requiredMark={customizeRequiredMark}
            layout="vertical"
            onFinish={handleSubmit}
            onFieldsChange={(changedFields) => {
              // Only update form changed state if any field was actually changed
              const hasChanged = changedFields.some((field) => field.touched);
              if (hasChanged !== isFormChanged) {
                setIsFormChanged(hasChanged);
              }
            }}
            preserve={true} // Preserve form values on re-render
          >
            <Row gutter={[24, 24]}>
              {/* Left Column - Profile Photo */}
              <Col xs={24} md={8}>
                <div className="flex flex-col items-center">
                  <div className="w-full max-w-[323px] flex flex-col items-center">
                    <div className="w-full aspect-square max-w-[323px] flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg border border-gray-200 shadow-inner overflow-hidden">
                      {previewImage ? (
                        <img
                          src={previewImage}
                          alt={t('profile-settings.form.photoAlt')}
                          className="w-full h-full object-cover rounded-lg"
                        />
                      ) : userInfo?.photo ? (
                        <img
                          src={userInfo.photo}
                          alt={t('profile-settings.form.photoAlt')}
                          className="w-full h-full object-cover rounded-lg"
                        />
                      ) : (
                        <UserOutlined style={{ fontSize: 'min(64px, 8vw)', color: '#bdbdbd' }} />
                      )}
                    </div>
                    <Form.Item
                      valuePropName="fileList"
                      name="profilePicture"
                      getValueFromEvent={getFile}
                      extra={
                        <span className="text-gray-600 text-sm italic">
                          {t('common.uploadFormatWithoutPdf', { maxSize: MAX_FILE_SIZE_MB })}
                        </span>
                      }
                      className="mb-0 w-full mt-4"
                    >
                      <Upload
                        className="upload-custom-width w-full"
                        beforeUpload={(file) => {
                          validateUploadSize(file, t, false, 'images');
                          handlePreview(file);
                          return false; // Prevent auto upload
                        }}
                        accept={IMAGE_TYPE_WITHOUT_PDF}
                        maxCount={MAX_PROFILE_PICTURE_COUNT}
                        data-testid="profile-picture-upload"
                        showUploadList={false}
                        onChange={(info) => {
                          if (info.fileList.length > 0) {
                            handlePreview(info.fileList[0]);
                          } else {
                            setPreviewImage(null);
                          }
                          form.setFieldsValue({ _formChanged: true });
                        }}
                      >
                        <Button
                          {...fieldDefaultProps}
                          color="primary"
                          variant="outlined"
                          size="large"
                          icon={<UploadOutlined />}
                          data-testid="change-photo-button"
                          className="w-full"
                          block
                        >
                          <span className="h5-semibold">{t('profile-settings.form.changePhoto')}</span>
                        </Button>
                      </Upload>
                    </Form.Item>
                  </div>
                </div>
              </Col>

              {/* Right Column - Form Fields */}
              <Col xs={24} md={16}>
                <Row className="gap-y-5">
                  <Col xs={24}>
                    <Form.Item
                      label={t('profile-settings.form.firstName')}
                      name="name"
                      required={true}
                      className="mb-0"
                    >
                      <Input {...fieldDefaultProps} className="ant-input" disabled />
                    </Form.Item>
                  </Col>
                  <Col xs={24}>
                    <Form.Item
                      label={t('profile-settings.form.lastName')}
                      name="surname"
                      required={true}
                      className="mb-0"
                    >
                      <Input {...fieldDefaultProps} className="ant-input" disabled />
                    </Form.Item>
                  </Col>
                  <Col xs={24}>
                    <Form.Item label={t('profile-settings.form.email')} name="email" required={true} className="mb-0">
                      <Input {...fieldDefaultProps} className="ant-input" disabled />
                    </Form.Item>
                  </Col>
                  <Col xs={24}>
                    <ContactInput
                      form={form}
                      countries={countries?.data || []}
                      name="contactNumber"
                      label={t('profile-settings.form.contactNumber')}
                      required={false}
                      requiredMessage={t('profile-settings.form.rules.requiredContactNumber')}
                      selectedCountry={selectedCountry}
                      setSelectedCountry={setSelectedCountry}
                      dataTestId="contact-number-input"
                    />
                  </Col>
                  <Col xs={24}>
                    <Form.Item
                      label={t('profile-settings.form.department')}
                      name="department"
                      required={true}
                      className="-mt-5 mb-0"
                    >
                      <Input {...fieldDefaultProps} className="ant-input" disabled />
                    </Form.Item>
                  </Col>
                  <Col xs={24}>
                    <Form.Item
                      label={t('profile-settings.form.employeeId')}
                      name="employeeId"
                      required={true}
                      className="mb-0"
                    >
                      <Input {...fieldDefaultProps} className="ant-input" disabled />
                    </Form.Item>
                  </Col>
                  <Col xs={24}>
                    <Form.Item noStyle shouldUpdate>
                      {() => (
                        <div className="flex gap-4 mt-5 justify-end">
                          <Button
                            type="primary"
                            htmlType="submit"
                            {...fieldDefaultProps}
                            className="bg-blue-800 w-full sm:max-w-36"
                            data-testid="save-changes-button"
                            disabled={!isFormChanged || updateMutation.isPending}
                            loading={updateMutation.isPending}
                          >
                            {t('profile-settings.form.saveChanges')}
                          </Button>
                          {isFormChanged && (
                            <Button
                              type="default"
                              {...fieldDefaultProps}
                              className="w-full sm:max-w-36"
                              data-testid="discard-changes-button"
                              onClick={() => setIsDiscardModalOpen(true)}
                              disabled={updateMutation.isPending}
                            >
                              {t('profile-settings.form.discardChanges')}
                            </Button>
                          )}
                        </div>
                      )}
                    </Form.Item>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Form>
        )}
      </Card>

      <Card>
        <Row gutter={[24, 0]}>
          <Col xs={24}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="h3-bold " data-testid="sso-title">
                {t('profile-settings.sso.title')}
              </h3>
              <Form.Item label="" name="sso" valuePropName="checked" className="mb-0">
                <Button
                  className="mb-0"
                  color="primary"
                  variant="outlined"
                  data-testid="sso-enable-button"
                  size="large"
                  type={ssoState.ssoEnabled ? 'default' : 'primary'}
                  onClick={handleSSOToggle}
                  disabled={ssoState.ssoEnabled && !ssoState.removeSso}
                >
                  <span className="h5-semibold">{t('profile-settings.sso.button')}</span>
                </Button>
              </Form.Item>
            </div>
            <div className="flex flex-col gap-1">
              <p className="body-regular" data-testid="sso-description">
                {t('profile-settings.sso.info')}
              </p>
              {ssoState.ssoEnabled && !ssoState.removeSso && (
                <div className="relative max-w-md">
                  <Button
                    icon={<MicrosoftLogo />}
                    className="justify-start footnote-dec mt-4"
                    data-testid="sso-button"
                    type="default"
                    block
                    size="large"
                  >
                    {t('profile-settings.sso.button')}
                  </Button>
                  <Button
                    icon={<CloseOutlined />}
                    className="absolute right-2 top-1/2 -translate-y-1/4"
                    type="text"
                    onClick={handleSSODisable}
                    data-testid="remove-sso-button"
                  />
                </div>
              )}
            </div>
          </Col>
        </Row>
      </Card>

      <Card>
        <Row gutter={[24, 0]}>
          <Col xs={24}>
            <div className="flex justify-between items-center mb-4">
              <span className="h3-bold">{t('profile-settings.password.title')}</span>
              <Button
                color="primary"
                variant="outlined"
                size="large"
                data-testid="change-password-button"
                onClick={() => navigate(`/profile/change-password`)}
              >
                <span className="h5-semibold">{t('profile-settings.password.button')}</span>
              </Button>
            </div>
            <p className="body-regular" data-testid="password-description">
              {t('profile-settings.password.info', {
                date: userInfo?.passwordLastChanged
                  ? dayjs(userInfo.passwordLastChanged).format(DATE_FORMAT)
                  : t('profile-settings.password.neverChanged'),
              })}
            </p>
          </Col>
        </Row>
      </Card>

      <ConfirmationModal
        isOpen={isDiscardModalOpen}
        onClose={() => setIsDiscardModalOpen(false)}
        onConfirm={handleDiscard}
        title={t('profile-settings.discardChanges')}
        confirmText={t('profile-settings.sso.confirm')}
        cancelText={t('profile-settings.sso.cancel')}
      />
    </div>
  );
}
